import { Link } from "react-router-dom";
//styles
import styled from "styled-components";
//files and images
import { privacyPolicy } from "../files";
import { fb, insta, rzetelna, maps, calendar } from "../img";
//constants
import {
  calendlyUrl,
  facebookUrl,
  instagramUrl,
  mapsUrl,
  rzetelnaUrl,
} from "../constants/links";
//languages
import { useLanguage } from "../hooks/useLanguage";
import type { LanguageProps } from "../typings";
import { colors } from "../styles/variables";

const Footer = ({ language }: LanguageProps) => {
  const lang = useLanguage(language);

  return (
    <StyledFooter>
      <StyledFooterContainer>
        <div className="columns-container">
          <div className="firstCol">
            <h3>{lang.footer.linksTitle}</h3>
            <Link to="/">{lang.nav.home}</Link>
            <Link to="/services">{lang.nav.services}</Link>
            <Link to="/faq">{lang.nav.knowledge}</Link>
            <Link to="/contact">{lang.nav.contact}</Link>
            <Link target="blank" to={privacyPolicy}>
              {lang.nav.privacyPolicy}
            </Link>
            <div className="social">
              <a href={calendlyUrl} target="blank">
                <img src={calendar} alt="Calendly" loading="lazy" />
              </a>
              <a href={facebookUrl} target="blank">
                <img src={fb} alt="Facebook" loading="lazy" />
              </a>
              <a href={instagramUrl} target="blank">
                <img src={insta} alt="Instagram" loading="lazy" />
              </a>
              <a href={mapsUrl} target="blank">
                <img src={maps} alt="Google Maps" loading="lazy" />
              </a>
            </div>
          </div>
          <div className="secondCol">
            <h3>{lang.footer.infoTitle}</h3>
            <h4 id="title">AgwiPol Sp. z o.o.</h4>
            <p>NIP: 7010645357</p>
            <p>REGON: 366158708</p>
            <p>KRS: 0000655136</p>
            <a href={rzetelnaUrl} target="blank">
              <img src={rzetelna} alt="Rzetelna Firma" loading="lazy" />
            </a>
          </div>
          <div className="thirdCol">
            <h3>{lang.footer.contactsTitle}</h3>
            <div className="address-container">
              <h4>{lang.contact.address}</h4>
              <a href={mapsUrl} target="blank">
                ul. Kard. St. Wyszyńskiego 2/1,
                <br />
                lok. 302-303,
                <br />
                15-888 Białystok
              </a>
            </div>
            <div className="phone-container">
              <h4>{lang.contact.phones}</h4>
              <a href="tel:+48574497249">+48 574 497 249</a>
              <a href="tel:+48578290011">+48 578 290 011</a>
            </div>
            <div className="email-container">
              <h4>{lang.contact.email}</h4>
              <a href="mailto:biuro@agwipol.pl">biuro@agwipol.pl</a>
            </div>
            <div className="mobile-container">
              <h4>{lang.footer.linksTitle}</h4>
              <a href={calendlyUrl} target="blank">
                Calendly
              </a>
              <a href={facebookUrl} target="blank">
                Facebook
              </a>
              <a href={instagramUrl} target="blank">
                Instagram
              </a>
              <a href={privacyPolicy} target="blank">
                {lang.nav.privacyPolicy}
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>{`Copyright © ${new Date().getFullYear()} All rights reserved`}</p>
        </div>
      </StyledFooterContainer>
    </StyledFooter>
  );
};

//styles
const StyledFooter = styled.footer`
  background-color: ${colors.dark};
  display: flex;
  justify-content: center;
  * {
    transition: all 0.3s;
  }
`;

const StyledFooterContainer = styled.div`
  height: 100%;
  width: 1400px;
  display: flex;
  flex-direction: column;
  h3 {
    font-size: 1rem;
    text-align: left;
    margin-bottom: 1rem;
  }
  #title {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  p,
  a {
    font-size: 1rem;
    color: ${colors.gray};
    &:hover {
      color: ${colors.primary};
    }
  }
  .columns-container {
    height: 100%;
    padding: 3rem 0;
    display: flex;
    flex-wrap: wrap;
    p:hover {
      cursor: default;
    }
    .firstCol,
    .secondCol,
    .thirdCol {
      flex: 1;
      padding: 0 2rem;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .firstCol {
      .social {
        display: flex;
        align-items: center;
        a {
          width: 3rem;
          height: 3rem;
          margin-right: 1.5rem;
          img {
            width: 100%;
            height: 100%;
            object-fit: fit;
            &:hover {
              transform: scale(1.3);
              -webkit-box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.2);
              -moz-box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.2);
              box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.2);
            }
          }
        }
      }
    }
    .secondCol {
      img {
        width: 50%;
        object-fit: contain;
      }
    }
    .thirdCol {
      h4 {
        font-size: 1rem;
        margin-bottom: 0.5rem;
      }
      .phone-container {
        margin: 1rem 0;
        display: flex;
        flex-direction: column;
        p {
          margin: 0.2rem 0;
        }
      }
      .mobile-container {
        display: none;
      }
    }
  }
  .copyright {
    width: 100%;
    padding: 1rem 0;
    border-top: 1px solid ${colors.gray};
    p {
      text-align: center;
      &:hover {
        cursor: default;
        color: ${colors.gray};
      }
    }
  }
  @media all and (max-width: 1600px) {
    width: 1000px;
  }
  @media all and (max-width: 1144px) {
    width: 90vw;
  }
  @media all and (max-width: 768px) {
    width: 95vw;
    .columns-container {
      padding: 1.5rem 0;
      flex-direction: column;
      justify-content: center;
      .firstCol,
      .secondCol {
        display: none;
      }
      .thirdCol {
        padding: 0 1rem;
        align-items: center;
        h3 {
          font-size: 1.5rem;
        }
        .mobile-container {
          display: flex;
          margin-top: 1rem;
          flex-direction: column;
          a {
            margin: 0.2rem 0;
          }
        }
        .address-container,
        .email-container,
        .phone-container,
        .mobile-container {
          text-align: center;
        }
        .phone-container {
          p {
            text-align: center;
          }
        }
      }
    }
    .copyright {
      padding: 0.5rem 0;
    }
  }
`;

export default Footer;
